"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useTranslations } from "next-intl";
import { FadeUp } from "@/components/ui/section";

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

function NumCircle({ num, delay, inView }) {
  return (
    <motion.div
      className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center text-white font-mono font-bold text-sm flex-shrink-0 shadow-lg shadow-violet-500/40 z-10"
      initial={{ scale: 0, opacity: 0 }}
      animate={inView ? { scale: 1, opacity: 1 } : {}}
      transition={{ delay, duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
    >
      {num}
    </motion.div>
  );
}

function Connector({ delay, inView, height = 80 }) {
  return (
    <motion.div
      className="w-px bg-gradient-to-b from-violet-500 to-violet-500/20 flex-shrink-0"
      style={{ height }}
      initial={{ scaleY: 0, originY: "top" }}
      animate={inView ? { scaleY: 1 } : {}}
      transition={{ delay, duration: 0.5, ease: "easeOut" }}
    />
  );
}

function StepBox({ text, delay, inView, align = "left" }) {
  return (
    <motion.div
      className="rounded-2xl bg-zinc-900 border border-white/[0.06] px-8 py-5 w-[300px] lg:w-[380px]"
      initial={{ opacity: 0, x: align === "left" ? -16 : 16 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay, duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <p className="text-base font-semibold text-white leading-snug">{text}</p>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */

export default function ProjectApproach() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const t = useTranslations("home");

  const steps = t.raw("approaches");

  return (
    <section id="approach" className="relative overflow-hidden py-24 lg:py-32 bg-zinc-950">

      {/* Concentric circles background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 800 800" className="w-[90vw] h-[90vw] max-w-4xl" fill="none">
          <circle cx="400" cy="400" r="140" stroke="rgba(139,92,246,0.18)" strokeWidth="1" />
          <circle cx="400" cy="400" r="250" stroke="rgba(139,92,246,0.12)" strokeWidth="0.8" />
          <circle cx="400" cy="400" r="370" stroke="rgba(139,92,246,0.07)" strokeWidth="0.6" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-16 lg:mb-20">
          <h2 className="text-3xl lg:text-5xl font-bold text-white">
            {t("approachHeading")}
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-sm leading-relaxed">
            {t("approachDescription")}
          </p>
        </FadeUp>

        {/* ── Desktop diagram ──────────────────────────────── */}
        <div
          ref={ref}
          className="hidden md:flex items-start justify-center gap-6 lg:gap-12"
        >
          {/* LEFT GROUP — 01 and 02 */}
          <div className="flex items-start gap-4 flex-shrink-0">
            {/* Number column: circle → line → circle */}
            <div className="flex flex-col items-center pt-3">
              <NumCircle num="01" delay={0.1} inView={inView} />
              <Connector delay={0.4} inView={inView} height={84} />
              <NumCircle num="02" delay={0.7} inView={inView} />
            </div>
            {/* Boxes column */}
            <div className="flex flex-col gap-0">
              <div className="pt-1 pb-0">
                <StepBox text={steps[0]} delay={0.2} inView={inView} align="left" />
              </div>
              {/* gap matches line height + circle diameter */}
              <div style={{ height: 84 + 48 - 8 }} />
              <StepBox text={steps[1]} delay={0.8} inView={inView} align="left" />
            </div>
          </div>

          {/* CENTER CONNECTOR — horizontal bridge between 02 and 03 */}
          <div className="flex items-center self-center" style={{ marginTop: 84 + 48 + 8 }}>
            <motion.div
              className="h-px w-10 lg:w-16 bg-gradient-to-r from-violet-500/50 to-cyan-500/50"
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              style={{ originX: "left" }}
              transition={{ delay: 1.0, duration: 0.4 }}
            />
          </div>

          {/* RIGHT GROUP — 03 and 04, offset downward to align 03 with 02 */}
          <div
            className="flex items-start gap-4 flex-shrink-0"
            style={{ marginTop: 84 + 48 + 8 }}
          >
            <div className="flex flex-col items-center pt-3">
              <NumCircle num="03" delay={1.1} inView={inView} />
              <Connector delay={1.4} inView={inView} height={84} />
              <NumCircle num="04" delay={1.7} inView={inView} />
            </div>
            <div className="flex flex-col gap-0">
              <div className="pt-1">
                <StepBox text={steps[2]} delay={1.2} inView={inView} align="right" />
              </div>
              <div style={{ height: 84 + 48 - 8 }} />
              <StepBox text={steps[3]} delay={1.8} inView={inView} align="right" />
            </div>
          </div>
        </div>

        {/* ── Mobile: 2×2 numbered cards ─────────────────── */}
        <div className="grid grid-cols-2 gap-4 md:hidden">
          {["01", "02", "03", "04"].map((num, i) => (
            <div key={num} className="rounded-2xl bg-zinc-900 border border-white/[0.08] p-5">
              <div className="text-3xl font-mono font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                {num}
              </div>
              <p className="text-sm font-medium text-zinc-200 leading-tight">{steps[i]}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
